/**
 * The identity port.
 *
 * Minting an id is a platform capability in exactly the same way reading a clock
 * is: `crypto.randomUUID` is a browser global, and a surface that calls it
 * cannot be tested. Six surfaces had already worked around its absence by taking
 * an id function as a prop — `features/journal/goals/presentation.tsx`,
 * `features/journal/composer/seeds.ts`, `features/journal/editor/useJournalEditor.ts`
 * and three under `features/train` and `features/build` — each with its own
 * `?? (() => crypto.randomUUID())` fallback, which is the same browser global
 * reached through six different doors. This port is the one door.
 *
 * The minting functions return the **branded** id types rather than strings,
 * because the failure a shared id source invites is the one branding exists to
 * stop: an entry id stored where a goal id belongs. `JournalEntryId` and
 * `JournalGoalId` are both UUID-shaped strings, they are both minted by the same
 * call underneath, and nothing but the type distinguishes them at the point of
 * use. A `mintId(): string` port would typecheck at every one of those call
 * sites and would have caught none of it.
 *
 * The combo and workout kinds were added after the first build, when the
 * `quality/import-boundaries` gate found Combo Lab, Combo Studio, the Workout
 * Tape and the plan library each defaulting to `crypto.randomUUID()` because the
 * port covered only the Journal and effort records. A local escape in four
 * surfaces is the shape this port exists to prevent, so the port grew rather
 * than the allowlist.
 */

import type {
  ComboBeatId,
  ComboId,
  PhraseBracketId,
  ResponseBranchId,
  ResponseOptionId,
} from "../combat";
import type { EffortCheckpointId, EffortSessionId } from "../effort";
import type {
  JournalBlockId,
  JournalEntryId,
  JournalGoalId,
  JournalStagedMediaId,
} from "../journal";
import type {
  ActivityId,
  WorkoutIntervalId,
  WorkoutPlanId,
  WorkoutSectionId,
} from "../planning";

/**
 * The kinds of identity this port mints, sorted as the port's members are.
 *
 * Named as a value so a conformance suite, a fake and a diagnostic can iterate
 * every kind rather than restating six cases each — a suite that enumerated its
 * own list would stop covering a kind added later, silently, which is the exact
 * failure a conformance suite exists to prevent.
 */
export const ID_KINDS = [
  "activity",
  "combo",
  "comboBeat",
  "effortCheckpoint",
  "effortSession",
  "journalBlock",
  "journalEntry",
  "journalGoal",
  "journalStagedMedia",
  "phraseBracket",
  "responseBranch",
  "responseOption",
  "workoutInterval",
  "workoutPlan",
  "workoutSection",
] as const;

export type IdKind = (typeof ID_KINDS)[number];

/**
 * The port's member name for a kind: `"journalGoal"` mints through `journalGoalId()`.
 *
 * A derived name rather than a hand-written table because the two lists drifting
 * apart would leave a kind that the suite believes it covers and does not.
 */
export type IdKindMember = `${IdKind}Id`;

export function idKindMember(kind: IdKind): IdKindMember {
  return `${kind}Id`;
}

/**
 * Mints identity. One capability, one branded result per kind.
 *
 * Every member is synchronous and total: a caller mints an id at the moment it
 * builds the record that carries it, inside a reducer or an event handler that
 * has nowhere to await. An implementation that could not produce an id would
 * make record creation itself failable, and no surface in the product has a
 * meaningful answer to "the id could not be minted".
 */
export interface IdPort {
  /** Identity for a user-defined activity (`customActivityDefinition`). */
  activityId(): ActivityId;
  /**
   * Identity for a combo score — the thing Combo Lab saves and Combo Studio
   * composes. Distinct from the beats inside it: a score id names a record in
   * the combo library, a beat id addresses a position within one.
   */
  comboId(): ComboId;
  /**
   * Identity for one beat of a score.
   *
   * The editing rules in `@ff/combo` cannot derive this. A duplicated beat and a
   * pasted chunk must differ from what they were copied from, so deriving a beat
   * id from the score id and an index produces a collision by construction —
   * which is why `ComboIdSource` exists there and why it is fed from here.
   */
  comboBeatId(): ComboBeatId;
  /**
   * Identity for a live effort checkpoint — deliberately distinct from the
   * session it belongs to, per `effort.ts`, because a checkpoint outlives the
   * document that wrote it and may never become a record at all.
   */
  effortCheckpointId(): EffortCheckpointId;
  /** Identity for an effort session, open or manually entered. */
  effortSessionId(): EffortSessionId;
  /**
   * Identity for one block of a journal document.
   *
   * Distinct from the entry that contains it because a block id is minted far
   * more often than an entry id — the rich editor mints one on every paragraph
   * split, and `journalProjection.ts` mints one for every pasted block whose
   * claimed id collides — while the entry keeps the id it was created under.
   * Blocks are also the only kind here that is not a record: they are addressed
   * inside a document, never stored under their own key.
   */
  journalBlockId(): JournalBlockId;
  /**
   * Identity for a journal entry. A draft is stored under the id of the entry it
   * shadows (`JournalDraftId` is an alias), so a draft never mints its own.
   */
  journalEntryId(): JournalEntryId;
  /** Identity for a journal goal. */
  journalGoalId(): JournalGoalId;
  /** Identity for a file staged during import or voice recording (F-JE-22). */
  journalStagedMediaId(): JournalStagedMediaId;
  /** Identity for a phrase bracket grouping beats inside a score. */
  phraseBracketId(): PhraseBracketId;
  /** Identity for a response branch hanging off a beat. */
  responseBranchId(): ResponseBranchId;
  /** Identity for one option inside a response branch. */
  responseOptionId(): ResponseOptionId;
  /**
   * Identity for one interval of a workout plan.
   *
   * The three workout kinds are separate for the same reason the journal's entry
   * and block are: `materializeWorkoutPlan` mints all three in one pass, and a
   * single `mintId(): string` there would let a section id be written into an
   * interval's field with nothing to notice it.
   */
  workoutIntervalId(): WorkoutIntervalId;
  /** Identity for a saved workout plan. */
  workoutPlanId(): WorkoutPlanId;
  /** Identity for a section of a workout plan. */
  workoutSectionId(): WorkoutSectionId;
}

/**
 * Builds an {@link IdPort} from one raw string source.
 *
 * The branding lives here, in the module that declares the port, rather than
 * being repeated in the fake and again in every adapter. That is not tidiness:
 * a cast is the one place the type system stops helping, so there is exactly one
 * of each, and an implementation supplies only the part it can genuinely differ
 * on — where the string comes from.
 *
 * `mintRaw` receives the kind so an implementation that wants per-kind sequences
 * (the fake does; a UUID source does not) can produce them without a second
 * factory.
 */
export function idPortFromSource(mintRaw: (kind: IdKind) => string): IdPort {
  return {
    activityId: () => mintRaw("activity") as ActivityId,
    comboId: () => mintRaw("combo") as ComboId,
    comboBeatId: () => mintRaw("comboBeat") as ComboBeatId,
    effortCheckpointId: () => mintRaw("effortCheckpoint") as EffortCheckpointId,
    effortSessionId: () => mintRaw("effortSession") as EffortSessionId,
    journalBlockId: () => mintRaw("journalBlock") as JournalBlockId,
    journalEntryId: () => mintRaw("journalEntry") as JournalEntryId,
    journalGoalId: () => mintRaw("journalGoal") as JournalGoalId,
    journalStagedMediaId: () => mintRaw("journalStagedMedia") as JournalStagedMediaId,
    phraseBracketId: () => mintRaw("phraseBracket") as PhraseBracketId,
    responseBranchId: () => mintRaw("responseBranch") as ResponseBranchId,
    responseOptionId: () => mintRaw("responseOption") as ResponseOptionId,
    workoutIntervalId: () => mintRaw("workoutInterval") as WorkoutIntervalId,
    workoutPlanId: () => mintRaw("workoutPlan") as WorkoutPlanId,
    workoutSectionId: () => mintRaw("workoutSection") as WorkoutSectionId,
  };
}
